import { StateUpdater, useState } from "preact/hooks";
import { data, emptyShoppingBasket, Product, ShoppingBasketEntry, ShoppingBasketState } from "./data";
import { calculatePrice } from "./pricecalculations";

const _state = {
    isLoaded: false,
    basket: {} as ShoppingBasketState,
    setBasket: null as StateUpdater<ShoppingBasketState> | null,
    timer: undefined as number | undefined,
};

function setOrIncrementProductCount(product: Product, count: number | undefined, delta: number | undefined, removeIfZero = true) {
    const newentries = [..._state.basket.entries];
    const index = newentries.findIndex(n => n.product.id === product.id);
    if (index === -1) {
        count = Math.max(0, count ?? delta ?? 0);
        if (count > 0 || !removeIfZero) {
            newentries.push({
                product,
                count: count ?? delta ?? 0
            });
        }
    } else {
        count = Math.max(0, count !== undefined ? count : (newentries[index].count + (delta ?? 0)));
        if (count > 0 || !removeIfZero) {
            newentries[index] = {
                product,
                count
            };
        }
        else {
            newentries.splice(index, 1);
        }
    }
    _state.setBasket?.({
        ..._state.basket,
        entries: newentries
    });

    schedulePersistBasket();
}

/** Host the shopping basket at the root of the component-tree */
export function useShoppingBasketPersistence() {
    let initialState = emptyShoppingBasket;
    if (!_state.isLoaded) {
        _state.isLoaded = true;
        initialState = getFromLocalstorage();
    }
    // todo: retrieve from localstorage the first time

    const [basket, setBasket] = useState<ShoppingBasketState>(initialState);
    _state.basket = basket;
    _state.setBasket = setBasket;

    // todo: a setBasket that also persists to localstorage
}

function schedulePersistBasket() {
    clearTimeout(_state.timer);
    _state.timer = window.setTimeout(() => {
        saveToLocalstorage(_state.basket);
        _state.timer = undefined;
    }, 500);
}

/** Access the shopping basket */
export function useShoppingBasket() {
    return { basket: _state.basket, setBasket: _state.setBasket!, setOrIncrementProductCount, setFromDate, setToDate };
}

export function setFromDate(date?: Date) {
    _state.setBasket?.({
        ..._state.basket,
        fromDate: date
    });
    schedulePersistBasket();
}

export function setToDate(date?: Date) {
    _state.setBasket?.({
        ..._state.basket,
        toDate: date
    });
    schedulePersistBasket();
}

export function calculateEntryPrice(entry: ShoppingBasketEntry) {

    const [peritem, itemtotal] = calculatePrice(entry.product, entry.count, _state.basket.fromDate, _state.basket.toDate);
    let peraddons = 0;

    if (entry.product.mandatoryAddons)
        for (let id of entry.product.mandatoryAddons) {
            let product = data.products.find(n => n.id == id);
            if (product) {
                peraddons += product.itemPrice ?? 0; // assuming mandatory addons are always one-off prices
            }
        }

    const addontotal = peraddons * entry.count;
    const total = itemtotal + addontotal;

    return { peritem, peraddons, itemtotal, addontotal, total };
}

function getFromLocalstorage() {
    const json = localStorage.getItem("basket");
    let state: ShoppingBasketState | null = null;
    try {
        state = json ? JSON.parse(json) : null;
        if (typeof state?.fromDate == "string") state.fromDate = new Date(state.fromDate);
        if (typeof state?.toDate == "string") state.toDate = new Date(state.toDate);
    } catch {
    }
    return !state || !state.entries ? emptyShoppingBasket : state;
}

function saveToLocalstorage(state: ShoppingBasketState) {
    if (!state || !state.entries || !state.entries.length) {
        localStorage.removeItem("basket")
    } else {
        const json = JSON.stringify(state);
        localStorage.setItem("basket", json)
    }
}

export function clearBasket() {
    localStorage.removeItem("basket");
    _state?.setBasket?.({ ...emptyShoppingBasket });
}
